import type { FormEvent } from 'react'
import type { ClientOffer } from 'types/seller'
import type { ClientResourceBody } from 'types'

import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import { useStore } from 'store'
import { offerStatuses, STATUS, URL_PARAM } from 'common'
import copy from 'copy.json'
import { clientPanelPaths } from 'paths'
import { replaceURLParams } from 'utils/urlParams'

import ContentHeader from 'components/ContentHeader'
import PageContainer from 'components/PageContainer'
import Scroll from 'components/Scroll'
import Spinner from 'components/Spinner'
import StatusesFilter from 'components/StatusesFilter'
import Table from 'components/Table'
import Button from 'components/Button'
import InputLabel from 'components/InputLabel'
import Input from 'components/Input'
import Pagination from 'components/Pagination'

import OfferDetailsRow from './OfferDetailsRow'

const text_form = copy.common.form
const text = copy.seller.details
const user_text = copy.components.clientPanelNavigation

export default function OfferDetailsScreen() {
  const timeout = useRef(0)
  const { api, clearFieldErrors } = useStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const defaultSearchValues: ClientResourceBody = {
    page_number: 0,
    statuses: [],
    electric_energy_meter_number: searchParams.get(URL_PARAM.electricEnergyMeterNumber),
    building_permit_number: searchParams.get(URL_PARAM.buildingPermitNumber)
  }

  const [currentClientOffers, setCurrentClientOffers] = useState<ClientOffer[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [filterStatuses, setFilterStatuses] = useState<ClientOffer['status'][]>([])
  const [searchValues, setSearchValues] = useState<ClientResourceBody>(defaultSearchValues)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const paginationIsVisible =
    currentClientOffers && currentClientOffers.length > 0 && totalPages > 1

  const getClientOffers = async () => {
    const response = await api.getClientResources('offer', searchValues)

    if (response) {
      setCurrentClientOffers(response.content)
      setCurrentPage(response.number)
      setTotalPages(response.total_pages)
    }
  }

  useEffect(() => {
    setLoading(true)

    getClientOffers()
      .then(() => setLoading(false))
      .catch(console.error)
  }, [searchValues]) // eslint-disable-line

  const handleChosenStatuses = (statuses: ClientOffer['status'][]) => {
    setFilterStatuses(statuses)
    setSearchValues(state => ({ ...state, statuses }))
  }

  const handleSearch = (event: FormEvent<HTMLInputElement>) => {
    clearFieldErrors(event.currentTarget.name)
    clearTimeout(timeout.current)

    const form = document.getElementById('search-seller-client-panel') as HTMLFormElement
    const values = Array.from(new FormData(form)).reduce(
      (result, [name, value]) => ({ ...result, [name]: value !== '' ? value : null }),
      {} as ClientResourceBody
    )

    timeout.current = window.setTimeout(
      () => setSearchValues(state => ({ ...state, ...values })),
      500
    )
  }

  const handlePageChange = (currentStep: number) => {
    replaceURLParams(currentStep, 'page', navigate)
    setSearchValues({ ...searchValues, page_number: currentStep - 1 })
  }

  if (!currentClientOffers) return null

  return (
    <PageContainer>
      <ContentHeader>
        <h1>{user_text.offer}</h1>
        <Button onClick={() => navigate(clientPanelPaths.additional_information)}>
          {text.add_new_resource}
        </Button>
      </ContentHeader>

      <form id="search-seller-client-panel">
        <StatusesFilterStyled statuses={offerStatuses} onChosenStatus={handleChosenStatuses}>
          <InputLabel>
            <span>{text_form.electric_energy_meter_number}</span>
            <Input
              name="electric_energy_meter_number"
              defaultValue={searchValues.electric_energy_meter_number || ''}
              onInput={handleSearch}
            />
          </InputLabel>

          <InputLabel>
            <span>{text_form.building_permit_number}</span>
            <Input
              name="building_permit_number"
              defaultValue={searchValues.building_permit_number || ''}
              onInput={handleSearch}
            />
          </InputLabel>
        </StatusesFilterStyled>
      </form>

      <Scroll>
        <Table>
          <thead>
            <tr>
              <th>{text_form.status}</th>
              <th>{text_form.energy_type}</th>
              <th>{text_form.distribution_name}</th>
              <th>{text_form.electric_energy_meter_number}</th>
              <th>{text_form.installation_power}</th>
              <th>{text_form.yearly_power_production}</th>
              <th>{text_form.energy_price}</th>
              <th />
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8}>
                  <Spinner style={{ margin: 'auto' }} />
                </td>
              </tr>
            ) : (
              <>
                {currentClientOffers?.map(currentOffer => (
                  <OfferDetailsRow
                    offer={currentOffer}
                    key={currentOffer.business_id}
                    onOfferDataRefresh={getClientOffers}
                  />
                ))}

                {!currentClientOffers.length && (
                  <tr>
                    <td colSpan={8}>
                      {filterStatuses.length > 0
                        ? `${text.not_found} ${filterStatuses
                            .map(status => STATUS[status])
                            .join(', ')}`
                        : text.not_found_any}
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </Table>
      </Scroll>

      {paginationIsVisible && (
        <StyledPagination
          numberOfSteps={totalPages}
          currentStep={currentPage + 1}
          onChange={handlePageChange}
        />
      )}
    </PageContainer>
  )
}

const StatusesFilterStyled = styled(StatusesFilter)`
  > div {
    width: 100%;
    max-width: 280px;
  }
`

const StyledPagination = styled(Pagination)`
  align-self: center;
`
